import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Brain, Upload, Loader2, FileText, CheckCircle2, AlertTriangle, X, ClipboardList, Download, Mail, PenLine, Save } from 'lucide-react';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Coerce toute valeur IA (parfois un objet/tableau) en texte affichable.
// Évite le crash React « Objects are not valid as a React child ».
const toText = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(toText).filter(Boolean).join(', ');
  if (typeof v === 'object') {
    for (const k of ['piece', 'libelle', 'label', 'nom', 'name', 'valeur', 'value', 'description', 'texte', 'montant']) {
      if (v[k] != null && typeof v[k] !== 'object') return String(v[k]);
    }
    try { return Object.values(v).filter((x) => x != null && typeof x !== 'object').map(String).join(' · '); }
    catch { return ''; }
  }
  return '';
};

const getAuthHeader = () => {
  const t = localStorage.getItem('token')
    || localStorage.getItem('access_token')
    || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Field = ({ label, value, confidence }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-slate-100 text-sm">
    <span className="text-slate-500 flex items-center gap-1.5">
      {confidence != null && <ConfidenceDot score={confidence} />}
      {label}
    </span>
    <span className="font-medium text-slate-800 text-right">{toText(value) || <span className="text-slate-300">—</span>}</span>
  </div>
);

const ConfidenceDot = ({ score }) => {
  const s = typeof score === 'number' ? score : 0;
  const color = s >= 0.75 ? 'bg-emerald-500' : s >= 0.4 ? 'bg-amber-500' : 'bg-red-400';
  const label = s >= 0.75 ? 'Fiabilité élevée' : s >= 0.4 ? 'Fiabilité moyenne' : 'À vérifier';
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${color}`}
      title={`${label} (${Math.round(s * 100)}%)`}
      data-testid="ia-confidence-dot"
    />
  );
};

export default function DOMIUMDocIAAnalysis({ onApplyToForm }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedRef, setGeneratedRef] = useState(null);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [sendingMail, setSendingMail] = useState(false);
  const [sendingSign, setSendingSign] = useState(false);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleAnalyse = async () => {
    if (files.length === 0) {
      toast.error('Ajoutez au moins un document');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      const res = await axios.post(`${API}/domium-ia/analyse-documents`, form, {
        headers: { ...getAuthHeader() },
      });
      setResult(res.data);
      toast.success('Analyse IA terminée');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur pendant l\'analyse IA';
      toast.error(typeof msg === 'string' ? msg : 'Erreur pendant l\'analyse IA');
    } finally {
      setLoading(false);
    }
  };

  const ext = result?.extraction || {};
  const identite = ext.identite || {};
  const operation = ext.operation || {};
  const intervenants = Array.isArray(ext.intervenants) ? ext.intervenants : [];
  const conf = ext.confidence || {};

  const buildDraft = () => {
    const num = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^\d.-]/g, ''));
      return Number.isFinite(n) ? n : null;
    };
    const cleanedOperation = { ...(ext.operation || {}) };
    // Champs numériques : jamais de chaîne vide (sinon 422 côté Pydantic)
    cleanedOperation.cout_global_ttc = num(operation.cout_global_ttc ?? ext.montants?.cout_total_construction_ht);
    if (cleanedOperation.cout_global_ttc === null) delete cleanedOperation.cout_global_ttc;
    return {
      identite: ext.identite || {},
      operation: cleanedOperation,
      intervenants: intervenants.map((it, idx) => ({
        id: Date.now() + idx,
        raison_sociale: toText(it.raison_sociale || it.nom),
        siret: toText(it.siret),
        role: toText(it.role || it.corps_etat),
        assurance_compagnie: toText(it.assurance_compagnie),
        assurance_numero_police: toText(it.assurance_numero_police),
        assurance_date_effet: toText(it.assurance_date_effet),
        assurance_reference_solidaire: toText(it.assurance_reference_solidaire),
      })),
    };
  };

  const handleApply = () => {
    if (onApplyToForm) {
      onApplyToForm(buildDraft());
      toast.success('Données envoyées au formulaire de souscription');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGeneratedRef(null);
    try {
      const draft = buildDraft();
      const res = await axios.post(`${API}/domium/subscribe/simulate`, {
        identite: draft.identite,
        operation: draft.operation,
        intervenants: draft.intervenants,
      }, { headers: getAuthHeader() });
      const ref = res.data?.reference || res.data?.simulation?.reference;
      if (!ref) throw new Error('no-ref');
      setGeneratedRef(ref);
      // Enregistrement automatique dans la GED (le simulateur a déjà persisté en base)
      try {
        await axios.post(`${API}/domium/subscribe/${ref}/sync-ged`, {}, { headers: getAuthHeader() });
        toast.success('Documents générés et enregistrés dans la GED (Devis + CP/CG)');
      } catch {
        toast.success('Documents générés (Devis + CP/CG). Synchronisation GED à vérifier.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur pendant la génération';
      toast.error(typeof msg === 'string' ? msg : 'Erreur pendant la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendMissing = async () => {
    if (!emailTo || !emailTo.includes('@')) {
      toast.error('Saisissez une adresse email valide');
      return;
    }
    setSendingMail(true);
    try {
      await axios.post(`${API}/domium-ia/send-missing-info`, {
        destinataire_email: emailTo,
        destinataire_nom: identite.representant_nom || identite.raison_sociale || '',
        pieces_manquantes: Array.isArray(ext.pieces_manquantes) ? ext.pieces_manquantes : [],
        resume: ext.resume || '',
        raison_sociale: identite.raison_sociale || '',
        reference: generatedRef || '',
      }, { headers: getAuthHeader() });
      toast.success(`Liste des pièces manquantes envoyée à ${emailTo}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur pendant l\'envoi';
      toast.error(typeof msg === 'string' ? msg : 'Erreur pendant l\'envoi');
    } finally {
      setSendingMail(false);
    }
  };

  const handleSendForSignature = async () => {
    if (!generatedRef) return;
    setSendingSign(true);
    try {
      await axios.post(`${API}/domium/signature/initiate`, {
        reference: generatedRef,
        signer_email: emailTo || undefined,
        signer_name: identite.representant_nom || identite.raison_sociale || undefined,
        method: 'email',
      }, { headers: getAuthHeader() });
      toast.success('Documents envoyés pour signature électronique (code par email)');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur envoi signature';
      toast.error(typeof msg === 'string' ? msg : 'Erreur envoi signature');
    } finally {
      setSendingSign(false);
    }
  };

  const handleSaveForLater = () => {
    // La souscription est déjà persistée en base au moment de la génération.
    toast.success(`Dossier enregistré${generatedRef ? ` (réf ${generatedRef})` : ''}. Vous pourrez le reprendre depuis « Suivi DOS DOMIUM ».`);
  };

  const downloadDoc = async (docType, filename) => {
    if (!generatedRef) return;
    setDownloadingDoc(docType);
    try {
      const res = await axios.get(`${API}/domium/documents/${generatedRef}/${docType}`, {
        headers: getAuthHeader(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', filename);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(`Erreur téléchargement ${filename}`);
    } finally {
      setDownloadingDoc(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="domium-ia-analysis">
      <Card className="border-violet-200 bg-gradient-to-br from-violet-50 via-white to-indigo-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-violet-800">
            <Brain className="h-5 w-5" />
            Analyse IA des documents de souscription
          </CardTitle>
          <p className="text-sm text-slate-600">
            Téléversez tous les documents du dossier — y compris une archive <strong>ZIP</strong>
            {' '}contenant plusieurs pièces (devis intervenants, permis de construire,
            attestations d'assurance, plans…). L'assistant IA extrait les données pour
            pré-remplir la souscription et liste les pièces manquantes. Aide au pré-remplissage,
            pas un avis juridique.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.txt,.zip,.png,.jpg,.jpeg"
            className="hidden"
            onChange={onPick}
            data-testid="domium-ia-file-input"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => inputRef.current?.click()}
              className="border-violet-300 text-violet-700 hover:bg-violet-50"
              data-testid="domium-ia-add-files-btn"
            >
              <Upload className="h-4 w-4 mr-2" /> Ajouter des documents ou un ZIP
            </Button>
            <Button
              onClick={handleAnalyse}
              disabled={loading || files.length === 0}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
              data-testid="domium-ia-analyse-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Brain className="h-4 w-4 mr-2" />}
              {loading ? 'Analyse en cours…' : (result ? 'Relancer l\'analyse' : 'Analyser avec l\'IA')}
            </Button>
          </div>

          {files.length > 0 && (
            <ul className="space-y-1" data-testid="domium-ia-file-list">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm bg-white rounded border border-slate-200 px-3 py-1.5">
                  <FileText className="h-4 w-4 text-slate-400" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-600" aria-label="Retirer">
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {result && Array.isArray(result.documents) && result.documents.length > 0 && (
        <Card data-testid="domium-ia-thumbnails">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-slate-700">
              <FileText className="h-4 w-4" /> Pièces détectées ({result.documents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {result.documents.map((d, i) => (
                <div key={i} className="flex flex-col items-center text-center" data-testid="domium-ia-thumb-item">
                  <div className="w-full aspect-[3/4] rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                    {d.thumbnail ? (
                      <img src={d.thumbnail} alt={d.filename} className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="h-8 w-8 text-slate-300" />
                    )}
                  </div>
                  <span className="mt-1 text-[11px] text-slate-600 truncate w-full" title={d.filename}>{d.filename}</span>
                  {d.extracted
                    ? <span className="text-[10px] text-emerald-600">{d.ocr ? 'OCR' : `${d.chars || 0} car.`}</span>
                    : <span className="text-[10px] text-red-400">non lu</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {result && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="domium-ia-result">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                <ClipboardList className="h-4 w-4" /> Données extraites
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h5 className="text-xs font-semibold text-violet-700 uppercase mb-1">Identité</h5>
                <Field label="Qualité" value={identite.qualite} />
                <Field label="Raison sociale" value={identite.raison_sociale} confidence={conf['identite.raison_sociale']} />
                <Field label="Nom / Prénom" value={[identite.nom, identite.prenom].filter(Boolean).join(' ')} />
                <Field label="SIRET" value={identite.siret} confidence={conf['identite.siret']} />
                <Field label="Représentant" value={identite.representant_nom} />
                <Field label="Ville" value={identite.ville} />
                <Field label="Email" value={identite.email} />
              </div>
              <div>
                <h5 className="text-xs font-semibold text-violet-700 uppercase mb-1">Opération</h5>
                <Field label="Type" value={operation.type_operation} confidence={conf['operation.type_operation']} />
                <Field label="Bâtiment" value={operation.type_batiment} />
                <Field label="Territoire" value={operation.territoire} confidence={conf['operation.territoire']} />
                <Field label="Chantier" value={operation.ville_chantier} />
                <Field label="Permis" value={operation.reference_permis} />
                <Field label="Coût global TTC" value={operation.cout_global_ttc} confidence={conf['operation.cout_global_ttc']} />
              </div>
              {intervenants.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-violet-700 uppercase mb-1 flex items-center gap-1.5">
                    {conf.intervenants != null && <ConfidenceDot score={conf.intervenants} />}
                    Intervenants ({intervenants.length})
                  </h5>
                  <ul className="space-y-1.5" data-testid="ia-intervenants-list">
                    {intervenants.map((it, i) => (
                      <li key={i} className="text-sm text-slate-700 border-b border-slate-100 py-1.5">
                        <div className="flex justify-between">
                          <span className="font-medium">{toText(it.raison_sociale || it.nom) || `Intervenant ${i + 1}`}</span>
                          <span className="text-slate-500 text-xs">{toText(it.role || it.corps_etat)}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {it.a_devis && <Badge variant="blue" className="text-[10px]">Devis</Badge>}
                          {it.a_attestation_assurance && <Badge variant="indigo" className="text-[10px]">Assurance</Badge>}
                          {it.a_attestation_coppam && <Badge variant="pink" className="text-[10px]">Attestation COPPAM</Badge>}
                          {it.assurance_compagnie && <span className="text-[10px] text-slate-400">{toText(it.assurance_compagnie)}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                <CheckCircle2 className="h-4 w-4" /> Complétude du dossier
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ext.resume && (
                <p className="text-sm text-slate-600 bg-slate-50 rounded p-3 border border-slate-200">{toText(ext.resume)}</p>
              )}
              {Array.isArray(ext.pieces_presentes) && ext.pieces_presentes.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-emerald-700 uppercase mb-2">Pièces présentes</h5>
                  <div className="flex flex-wrap gap-1.5">
                    {ext.pieces_presentes.map((p, i) => (
                      <Badge key={i} variant="success" className="text-xs whitespace-normal break-words text-left max-w-full items-start leading-snug py-1">
                        <CheckCircle2 className="h-3 w-3 mr-1 mt-0.5 shrink-0" /> <span>{toText(p)}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(ext.pieces_manquantes) && ext.pieces_manquantes.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-amber-700 uppercase mb-2">Pièces manquantes</h5>
                  <div className="flex flex-wrap gap-1.5">
                    {ext.pieces_manquantes.map((p, i) => (
                      <Badge key={i} variant="warning" className="text-xs whitespace-normal break-words text-left max-w-full items-start leading-snug py-1">
                        <AlertTriangle className="h-3 w-3 mr-1 mt-0.5 shrink-0" /> <span>{toText(p)}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-slate-400">
                Modèle : {result.model} · Documents analysés : {result.documents?.filter((d) => d.extracted).length}/{result.documents?.length}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {result && (
        <Card className="border-indigo-200 bg-indigo-50/40" data-testid="domium-ia-actions">
          <CardContent className="py-4 flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={handleApply}
              className="border-violet-300 text-violet-700 hover:bg-violet-100"
              data-testid="domium-ia-apply-btn"
            >
              <ClipboardList className="h-4 w-4 mr-2" /> Appliquer au formulaire
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
              data-testid="domium-ia-generate-btn"
            >
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              {generating ? 'Génération…' : 'Générer Devis + CP/CG'}
            </Button>
            {generatedRef && (
              <div className="flex flex-wrap items-center gap-2" data-testid="domium-ia-generated-docs">
                <Badge variant="success">
                  Réf : {generatedRef}
                </Badge>
                <Button
                  variant="outline" size="sm"
                  onClick={() => downloadDoc('devis-ess', `Devis_ESS_DOMIUM_${generatedRef}.pdf`)}
                  disabled={!!downloadingDoc}
                  data-testid="domium-ia-dl-devis-btn"
                >
                  {downloadingDoc === 'devis-ess' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                  Devis ESS
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => downloadDoc('dossier-maitre', `Dossier_Maitre_CP_CG_${generatedRef}.pdf`)}
                  disabled={!!downloadingDoc}
                  data-testid="domium-ia-dl-cpcg-btn"
                >
                  {downloadingDoc === 'dossier-maitre' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                  Dossier Maître CP/CG
                </Button>
              </div>
            )}
          </CardContent>
          <CardContent className="pt-0 pb-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                <Mail className="h-4 w-4 text-slate-400" />
                <Input
                  type="email"
                  placeholder="email du futur membre"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  className="max-w-xs"
                  data-testid="domium-ia-email-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleSendMissing}
                disabled={sendingMail}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="domium-ia-send-missing-btn"
              >
                {sendingMail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Envoyer la liste des manquants (PDF)
              </Button>
            </div>
            {generatedRef && (
              <div className="flex flex-wrap items-center gap-2 border-t border-indigo-100 pt-3">
                <Button
                  onClick={handleSendForSignature}
                  disabled={sendingSign}
                  className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                  data-testid="domium-ia-send-signature-btn"
                >
                  {sendingSign ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PenLine className="h-4 w-4 mr-2" />}
                  Envoyer pour signature électronique
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSaveForLater}
                  data-testid="domium-ia-save-later-btn"
                >
                  <Save className="h-4 w-4 mr-2" /> Enregistrer pour plus tard
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
